/** TS port of discovr's internal/campaigns/amount_parse.go parseAmount —
 *  keep the two in sync by hand (no shared build tooling across the repo
 *  boundary); see amount_parse_test.go / testdata/amount_parse_cases.json
 *  for the cross-language parity fixture. */
import { stripAllInvisibleChars, trimSpace } from './invisibleChars';

export type AmountKind = 'price' | 'metric';

/** Longest token first so "crores"/"lakhs"/"million" aren't shadowed by
 *  "cr"/"l"/"m". "mil" is deliberately not a unit: Spanish and Portuguese
 *  write it for a thousand. A metric-only unit is not read in a price cell: no
 *  creator charges a billion. Mirrors the backend's suffixMultipliers. */
const SUFFIX_MULTIPLIERS: ReadonlyArray<{ token: string; mult: number; metricOnly: boolean }> = [
  { token: 'million', mult: 1_000_000, metricOnly: false },
  { token: 'billion', mult: 1_000_000_000, metricOnly: true },
  { token: 'crores', mult: 10_000_000, metricOnly: false },
  { token: 'crore', mult: 10_000_000, metricOnly: false },
  { token: 'lakhs', mult: 100_000, metricOnly: false },
  { token: 'lakh', mult: 100_000, metricOnly: false },
  { token: 'lacs', mult: 100_000, metricOnly: false },
  { token: 'lac', mult: 100_000, metricOnly: false },
  { token: 'mn', mult: 1_000_000, metricOnly: false },
  { token: 'bn', mult: 1_000_000_000, metricOnly: true },
  { token: 'cr', mult: 10_000_000, metricOnly: false },
  { token: 'k', mult: 1_000, metricOnly: false },
  { token: 'm', mult: 1_000_000, metricOnly: false },
  { token: 'l', mult: 100_000, metricOnly: false },
];

const NUMERIC_PATTERN = /^\d+(\.\d+)?$/;

/** A currency word typed in place of a symbol at the start of a PRICE cell
 *  ("Rs 5000", "Rs. 5,000", "INR 5000", "Rupees 5000"), matched after
 *  lowercasing. Mirrors the backend's currency-word prefix. */
const CURRENCY_WORD_PREFIX = /^(?:rs\.?|inr|rupees?)\s*/;
/** The same word at the end of a PRICE cell ("25000 INR", "25,000 Rs",
 *  "25000 rs."). It must not continue a longer word ("5 hrs" is not "5 h" plus
 *  "rs"), so a letter before it blocks the match; group 1 is what precedes it
 *  and is kept. Mirrors the backend's trailingCurrencyWordRE. */
const CURRENCY_WORD_SUFFIX = /(^|[^a-z])(?:rs\.?|inr|rupees?)$/;
/** Every Unicode space the backend's unicode.IsSpace drops: JS's `\s` plus
 *  U+0085. A sheet exported from Excel or the web groups thousands with a
 *  non-breaking space (U+00A0), narrow no-break space (U+202F) or thin space
 *  (U+2009); all read as a plain space. */
const ANY_SPACE = /[\s\u0085]/g;

/** The only comma groupings a cell may use: Western (1,000,000) or Indian
 *  (12,34,567), with an optional decimal part. Anything else ("1,5",
 *  "1.234,56") is ambiguous — a decimal comma or a typo — so it must not be
 *  read by silently dropping the commas. The first group never starts with a
 *  zero ("0,075" is a decimal comma, not 75). Prices and metrics share it.
 *  Mirrors the backend's grouping rule. */
const GROUPED_DIGITS_PATTERN = /^(?:[1-9]\d{0,2}(?:,\d{3})+|[1-9]\d?(?:,\d{2})*,\d{3})(?:\.\d+)?$/;
const GROUP_SEPARATOR = ',';

/** Gate applied only to the metric path's bare `Number(rest)` call. The
 *  metric path is deliberately lenient (accepts scientific notation, e.g.
 *  "1e3") in a way the strict price path isn't — but JS's `Number()` also
 *  accepts non-decimal literal syntax ("0x10" -> 16, "0b101" -> 5,
 *  "0o17" -> 15) that Go's strconv.ParseFloat does not (no binary/octal
 *  float support at all, and hex requires a "p" exponent). Without this
 *  gate, a metric cell like "0x10" would parse successfully here but be
 *  rejected server-side — a parity gap against this file's "mirrors the
 *  backend exactly" contract.
 *
 *  Accepts plain decimal and decimal-with-exponent, with an optional
 *  leading sign — ParseFloat accepts a leading "+"/"-", and the "-" case is
 *  still caught by the n<0 check below, so this pattern must not reject it.
 *  Also accepts a bare-decimal form on either side (".5" or "5.") — Go's
 *  ParseFloat(".5",64) and ParseFloat("5.",64) both succeed (verified via
 *  `go run`), so a tighter \d+(\.\d+)? here would be a REVERSE parity gap:
 *  rejecting input the backend would accept.
 *
 *  Known, accepted divergence: Go's ParseFloat also accepts single
 *  underscore digit-separators ("1_000" -> 1000), but JS's `Number()`
 *  does not (Number("1_000") is NaN — separators are a source-literal
 *  syntax, not a string-parsing one) — closing that gap needs stripping
 *  underscores before Number(), a separate code path, not just this regex.
 *  Not worth it: nobody types "1_000" as a view count in a spreadsheet. */
const METRIC_NUMERIC_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i;

/** Defensive cap on a sheet cell's raw length before it reaches the
 *  regexes/Number() below. Not a real risk today (no catastrophic-
 *  backtracking regex here, and Number() parsing is linear), but raw is
 *  untrusted user-uploaded spreadsheet data, and no legitimate price/metric
 *  cell is anywhere close to this long. Mirrors the backend's
 *  maxRawLength. */
export const MAX_RAW_LENGTH = 64;

const UTF8_ENCODER = new TextEncoder();

/** The length the backend measures: Go's len(string) counts UTF-8 bytes, where
 *  a JS string's `length` counts UTF-16 units. */
function utf8ByteLength(text: string): number {
  return UTF8_ENCODER.encode(text).length;
}

/** Generous sanity ceiling on any sheet-import number (price or metric) —
 *  real creator deal prices, view counts, and engagement rates never
 *  approach this. Purpose is defense-in-depth: closes the gap where a
 *  legitimate-looking but extreme finite value (e.g. scientific notation on
 *  the deliberately-lenient metric path, or an absurd all-digit price)
 *  could overflow unguarded downstream aggregation (roster reach/cost
 *  totals) even though the finite check below lets it through as "valid."
 *  Mirrors the backend's maxParsedAmount. */
export const MAX_PARSED_AMOUNT = 1_000_000_000; // 1 billion

/** Strips Unicode "Currency Symbol" runes (category Sc — ₹ $ € £ ¥ ₮ ₸ ₼ ₾
 *  ₭ ¤ and every other currency symbol) plus every Unicode space, like the
 *  backend's stripSpaceAndCurrency. Keeps the commas so ungroupDigits can
 *  check them. */
function stripMetricDecoration(s: string): string {
  return s
    .replace(/\p{Sc}/gu, '')
    .replace(ANY_SPACE, '');
}

/** Like stripMetricDecoration, but drops the commas too: only for reading a
 *  cell's decimal places (sheetPrecisionUnit), never for its value. */
function stripCurrencySymbols(s: string): string {
  return stripMetricDecoration(s).replaceAll(GROUP_SEPARATOR, '');
}

/** The PRICE-cell counterpart of stripMetricDecoration: also drops a leading
 *  or trailing currency word. */
function stripPriceDecoration(s: string): string {
  return stripMetricDecoration(trimSpace(s.replace(CURRENCY_WORD_PREFIX, '')).replace(CURRENCY_WORD_SUFFIX, '$1'));
}

/** A cell's digits without their thousands separators, or null when the
 *  commas are not a valid grouping. Comma-free digits pass through untouched. */
function ungroupDigits(digits: string): string | null {
  if (!digits.includes(GROUP_SEPARATOR)) return digits;
  if (!GROUPED_DIGITS_PATTERN.test(digits)) return null;
  return digits.replaceAll(GROUP_SEPARATOR, '');
}

function stripSuffixMultiplier(s: string, kind: AmountKind): { rest: string; mult: number } {
  for (const { token, mult, metricOnly } of SUFFIX_MULTIPLIERS) {
    if (metricOnly && kind !== 'metric') continue;
    if (s.endsWith(token)) {
      return { rest: s.slice(0, -token.length), mult };
    }
  }
  return { rest: s, mult: 1 };
}

/** Parses a free-text sheet cell into a number, mirroring the backend's
 *  parseAmount exactly. 'price' is strict (\d+(\.\d+)? only, no scientific
 *  notation, commas only as Western or Indian thousands grouping), accepts a
 *  leading currency word and is sign-aware (preserves a leading "-"); 'metric'
 *  takes none of those price rules, is lenient (accepts scientific notation)
 *  and rejects negative instead; this asymmetry is intentional, matching the
 *  backend's own split. Commas follow the same grouping rule on both kinds, so
 *  a decimal comma ("4,5%") is unreadable, not 45. Checks for a
 *  non-finite result twice: right after parsing the bare number, and again
 *  after applying the suffix multiplier, since a huge-but-finite number
 *  times a multiplier can overflow to Infinity. */
export function parseAmount(raw: string, kind: AmountKind): number | null {
  if (utf8ByteLength(raw) > MAX_RAW_LENGTH) return null;

  const lowered = trimSpace(stripAllInvisibleChars(raw)).toLowerCase();
  let s = kind === 'price' ? stripPriceDecoration(lowered) : stripMetricDecoration(lowered);

  let neg = false;
  if (kind === 'price') {
    s = s.replace(/\/[-=]*$/, '');
    neg = s.startsWith('-');
    if (neg) s = s.slice(1);
  } else {
    s = s.endsWith('%') ? s.slice(0, -1) : s;
  }

  const { rest: digits, mult } = stripSuffixMultiplier(s, kind);
  const rest = ungroupDigits(digits);
  if (rest === null) return null;

  if (kind === 'price' && !NUMERIC_PATTERN.test(rest)) return null;
  if (kind === 'metric' && !METRIC_NUMERIC_PATTERN.test(rest)) return null;
  if (rest === '') return null;

  const n = Number(rest);
  if (!Number.isFinite(n)) return null;
  if (kind === 'metric' && n < 0) return null;

  const amount = n * mult;
  if (!Number.isFinite(amount) || Math.abs(amount) > MAX_PARSED_AMOUNT) return null;

  return neg ? -amount : amount;
}

/** The smallest step a metric cell expresses: "4.51" is 0.01, "48123" is 1,
 *  "48.1k" is 100. Mirrors the backend's sheetPrecisionUnit
 *  (import_review_rules.go). */
export function sheetPrecisionUnit(raw: string): number {
  const stripped = stripCurrencySymbols(raw.toLowerCase());
  const { rest, mult } = stripSuffixMultiplier(stripped.endsWith('%') ? stripped.slice(0, -1) : stripped, 'metric');
  const dot = rest.indexOf('.');
  if (dot < 0) return mult;
  return mult / 10 ** (rest.length - dot - 1);
}
